import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { CommonNavigate } from '@xemplo/common-routes';
import { CrossAppFeature } from '@xemplo/cross-app-flags';
import { useGetNavigationQueryV2 } from '@xemplo/directory-services-query';
import { useFeatureFlag } from '@xemplo/feature-flag';
import { safeCall } from '@xemplo/safe-call';
import { SupportedApps } from '@xemplo/types';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { NoAccessText, signedInAsText } from './no-access.constants';

const APPS_WITHOUT_PERSONA_SELECTION: SupportedApps[] = [
  SupportedApps.DawnDemo,
  SupportedApps.GP,
  SupportedApps.Embeddable,
  SupportedApps.HrHire,
];

export function useNoAccess(message?: string) {
  const { data } = useGetNavigationQueryV2();
  const { startAppUrl, appName, stalePersonaId } = useAppSettings();
  const { isEnabled } = useFeatureFlag();
  const navigate = useNavigate();

  const showExtras = useMemo(() => {
    // In order to show the extras, we must have a few elements:
    // 1. The feature must be enabled — HR apps ship an empty feature map, so a
    //    stale persona counts too: it is proof of one to switch to (FD-3050).
    // 2. We must have personas
    // 3. We must have a startAppUrl defined on the app init
    return (
      (isEnabled(CrossAppFeature.NoAccessOptions) || !!stalePersonaId) &&
      !!data?.personas?.length &&
      !!startAppUrl
    );
  }, [data?.personas?.length, stalePersonaId, isEnabled, startAppUrl]);

  const text = useMemo(() => {
    if (message) return message;

    // Naming the persona needs the nav data, which may not have landed yet.
    const persona = stalePersonaId ? data?.personaLookup?.[stalePersonaId] : undefined;
    const roles = persona?.roles.map((role) => role.name) ?? [];

    return [
      !!roles.length && signedInAsText(roles.join(', '), persona?.company?.name),
      stalePersonaId ? NoAccessText.OtherInstance : NoAccessText.Default,
      showExtras && NoAccessText.SelectPersona,
    ]
      .filter(Boolean)
      .join('\n');
  }, [data, stalePersonaId, showExtras, message]);

  const handleGoToPersonas = useCallback(() => {
    // Most of our react apps will have persona selection screen
    if (!APPS_WITHOUT_PERSONA_SELECTION.includes(appName)) {
      navigate(CommonNavigate.Persona, { replace: true });
      return;
    }

    // Defensive code. Unlikely to ever be wrong as it comes from the AzureDevOps
    // and it's unlikely it will ever change. But this is best practices.
    const [url] = safeCall(() => new URL(startAppUrl));
    if (!url?.protocol || !url?.hostname) return;

    window.location.href = startAppUrl;
  }, [appName, navigate, startAppUrl]);

  return { showExtras, text, handleGoToPersonas };
}
